import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { useDemoAutoLogin } from './hooks/useDemoAutoLogin';
import { DPassLogo } from './components/DPassLogo';

const Dashboard      = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const PassportList   = lazy(() => import('./pages/PassportList').then(m => ({ default: m.PassportList })));
const PassportCreator = lazy(() => import('./pages/PassportCreator').then(m => ({ default: m.PassportCreator })));
const PassportDetail = lazy(() => import('./pages/PassportDetail').then(m => ({ default: m.PassportDetail })));
const Compliance     = lazy(() => import('./pages/Compliance').then(m => ({ default: m.Compliance })));
const AuditLog       = lazy(() => import('./pages/AuditLog').then(m => ({ default: m.AuditLog })));
const Settings       = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const ImportCSV      = lazy(() => import('./pages/ImportCSV').then(m => ({ default: m.ImportCSV })));
const NotFound       = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));
const PublicViewer   = lazy(() => import('./pages/PublicViewer').then(m => ({ default: m.PublicViewer })));
const AccessRequest  = lazy(() => import('./pages/AccessRequest').then(m => ({ default: m.AccessRequest })));
const CBAM           = lazy(() => import('./pages/CBAM').then(m => ({ default: m.CBAM })));
const Repairability  = lazy(() => import('./pages/Repairability').then(m => ({ default: m.Repairability })));
const Packaging      = lazy(() => import('./pages/Packaging').then(m => ({ default: m.Packaging })));
const Docs           = lazy(() => import('./pages/Docs').then(m => ({ default: m.Docs })));

function LoadingScreen() {
  return (
    <div className="min-h-screen sidebar-gradient flex flex-col items-center justify-center gap-5">
      <div className="flex items-center gap-3 animate-fade-in">
        <DPassLogo className="w-12 h-12" />
        <div>
          <p className="text-white font-bold text-2xl tracking-tight">D-Pass</p>
          <p className="text-slate-500 text-sm">Digital Product Passports</p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-slate-500 text-sm animate-pulse">
        <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        Initialising…
      </div>
    </div>
  );
}

function App() {
  const { isReady } = useDemoAutoLogin();

  if (!isReady) return <LoadingScreen />;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login"          element={<Login />} />
        <Route path="/p/:dppId"       element={<PublicViewer />} />
        <Route path="/access-request" element={<AccessRequest />} />
        <Route path="/docs"           element={<Docs />} />
        <Route element={<Layout />}>
          <Route path="/"                element={<Dashboard />} />
          <Route path="/dashboard"       element={<Navigate to="/" replace />} />
          <Route path="/passports"       element={<PassportList />} />
          <Route path="/passports/new"   element={<PassportCreator />} />
          <Route path="/passports/:id"   element={<PassportDetail />} />
          <Route path="/import"          element={<ImportCSV />} />
          <Route path="/audit-log"       element={<AuditLog />} />
          <Route path="/compliance"      element={<Compliance />} />
          <Route path="/cbam"            element={<CBAM />} />
          <Route path="/repairability"   element={<Repairability />} />
          <Route path="/packaging"       element={<Packaging />} />
          <Route path="/settings"        element={<Settings />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
